import { Activity, Play, RefreshCw, Vibrate, Settings2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHapticEngine } from "../../hooks/useHapticsEngine";
import { DEFAULT_PRESETS, HapticPreset } from "../../hooks/useHapticsEngine/types"; // HapticPreset might be needed for casting

export const HapticsSettings = () => {
  // NOTE: `new AudioContext()` here is generally not ideal if this engine instance is meant to be the primary one.
  // The primary engine should be initialized with the AudioContext from useAudioPlayer.
  // For this component's scope of *editing* live config, this is workable for state management.
  const { settings, updateSettings, provideFeedback, isSupported, livePresetConfig, updateLivePresetConfig } = 
    useHapticEngine(new AudioContext());

  const [selectedPreset, setSelectedPreset] = useState(settings.currentPreset);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [isTestingHaptics, setIsTestingHaptics] = useState(false);
  const lastLogRef = useRef<string>("");

  // Update logs only when debug info actually changes
  useEffect(() => {
    if (showDebugInfo) {
      const debugInfo = JSON.stringify({ settings, livePresetConfig }, null, 2); // Include livePresetConfig
      if (debugInfo && debugInfo !== lastLogRef.current) {
        lastLogRef.current = debugInfo;
        setLogs((prev) => [...prev.slice(-49), debugInfo]); // Keep last 50 logs
      }
    }
  }, [showDebugInfo, settings, livePresetConfig]); // Added livePresetConfig

  const handlePresetChange = useCallback(
    (presetKey: string) => {
      setSelectedPreset(presetKey);
      updateSettings({ currentPreset: presetKey });
      // livePresetConfig will update via useEffect in useHapticEngine
    },
    [updateSettings]
  );
  
  const handleLiveConfigChange = useCallback((field: keyof HapticPreset, value: any, isNumeric = false, isNested = false, parentField?: keyof HapticPreset, nestedField?: string) => {
    if (!updateLivePresetConfig) return;

    let processedValue = isNumeric ? parseFloat(value) : value;
    if (isNumeric && isNaN(processedValue)) {
        processedValue = 0; // Or some other default / error handling
    }
    
    if (isNested && parentField && nestedField) {
        updateLivePresetConfig({
            [parentField]: {
                ...(livePresetConfig && livePresetConfig[parentField] as object), // Type assertion
                [nestedField]: processedValue,
            }
        } as Partial<HapticPreset>); // Cast to assure type correctness for nested update
    } else {
        updateLivePresetConfig({ [field]: processedValue } as Pick<HapticPreset, keyof HapticPreset>);
    }
  }, [updateLivePresetConfig, livePresetConfig]);


  const handleTestHaptics = useCallback(async () => {
    setIsTestingHaptics(true);
    try {
      // provideFeedback uses livePresetConfig internally if it's for the current preset
      await provideFeedback(Date.now() / 1000); 
    } finally {
      setIsTestingHaptics(false);
    }
  }, [provideFeedback]);

  // Simplified Input component for reuse
  const ConfigInput = ({ label, id, type = "number", value, onChange, min, max, step, nestedParent, nestedKey }: 
    { label: string, id: string, type?: string, value: any, onChange: (val: string) => void, min?: number, max?: number, step?: number, nestedParent?: keyof HapticPreset, nestedKey?: string }) => (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-zinc-300 mb-1">
        {label}
      </label>
      <input
        type={type}
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        max={max}
        step={step}
        className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500 text-sm text-white"
      />
    </div>
  );
  
  const fftOptions = [512, 1024, 2048, 4096, 8192];


  return (
    <div className="p-6 space-y-8">
      {/* Enable/Disable Switch */}
      <div className="flex items-center justify-between bg-zinc-800/50 p-4 rounded-lg">
        <div>
          <h3 className="font-semibold">Haptic Feedback</h3>
          <p className="text-sm text-zinc-400">Feel the rhythm with vibration feedback</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => updateSettings({ enabled: e.target.checked })}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
        </label>
      </div>

      {/* Test Haptics */}
      <div className="grid grid-cols-1 gap-4">
        <button
          onClick={handleTestHaptics}
          disabled={isTestingHaptics || !isSupported || !settings.enabled}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-yellow-500 hover:bg-yellow-600 rounded-lg transition disabled:opacity-50"
        >
          {isTestingHaptics ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Testing...
            </>
          ) : (
            <>
              <Play className="w-5 h-5" />
              Test Haptics
            </>
          )}
        </button>
      </div>

      {/* Presets Selection */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Vibrate className="w-5 h-5" />
          Preset Selection
        </h3>
        <div className="space-y-2">
          {Object.entries(DEFAULT_PRESETS).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => handlePresetChange(key)}
              className={`w-full px-4 py-3 text-left rounded-lg transition ${
                settings.currentPreset === key ? "bg-yellow-500 text-white" : "bg-zinc-800 hover:bg-zinc-700"
              }`}
            >
              <div className="font-medium capitalize">{preset.name}</div>
              <p className="text-xs opacity-80">{preset.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Live Preset Configuration */}
      {livePresetConfig && updateLivePresetConfig && settings.currentPreset === livePresetConfig.name && (
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Settings2 className="w-5 h-5" />
            Configure: {livePresetConfig.name}
          </h3>
          <div className="space-y-4 p-4 bg-zinc-800/50 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <ConfigInput label="Intensity" id="intensity" type="number" min={0} max={2} step={0.05} value={livePresetConfig.intensity} onChange={val => handleLiveConfigChange('intensity', val, true)} />
              <ConfigInput label="Duration (ms)" id="duration" type="number" min={10} max={1000} step={10} value={livePresetConfig.duration} onChange={val => handleLiveConfigChange('duration', val, true)} />
            </div>
            
            <h4 className="text-md font-semibold mt-4 pt-2 border-t border-zinc-700">Analysis Parameters</h4>
            <div className="grid grid-cols-2 gap-4">
              <ConfigInput label="Latency Comp. (ms)" id="latencyCompensation" type="number" min={0} max={500} step={10} value={livePresetConfig.latencyCompensation} onChange={val => handleLiveConfigChange('latencyCompensation', val, true)} />
              <div>
                <label htmlFor="fftSize" className="block text-sm font-medium text-zinc-300 mb-1">FFT Size</label>
                <select id="fftSize" value={livePresetConfig.fftSize} onChange={e => handleLiveConfigChange('fftSize', e.target.value, true)} className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-md shadow-sm focus:ring-yellow-500 focus:border-yellow-500 text-sm text-white">
                  {fftOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
              <ConfigInput label="Hop Size (samples)" id="hopSize" type="number" min={64} max={4096} step={64} value={livePresetConfig.hopSize} onChange={val => handleLiveConfigChange('hopSize', val, true)} />
              <ConfigInput label="Onset Sensitivity (0-2)" id="onsetSensitivity" type="number" min={0} max={2} step={0.05} value={livePresetConfig.onsetSensitivity} onChange={val => handleLiveConfigChange('onsetSensitivity', val, true)} />
              <ConfigInput label="Timing Window (s)" id="timingWindow" type="number" min={0.01} max={0.5} step={0.01} value={livePresetConfig.timingWindow} onChange={val => handleLiveConfigChange('timingWindow', val, true)} />
            </div>

            <h4 className="text-md font-semibold mt-4 pt-2 border-t border-zinc-700">Target Frequency Band (Hz)</h4>
            <div className="grid grid-cols-2 gap-4">
              <ConfigInput label="Min Frequency" id="tfbMin" type="number" min={20} max={20000} step={10} value={livePresetConfig.targetFrequencyBand?.min} onChange={val => handleLiveConfigChange('targetFrequencyBand', val, true, true, 'targetFrequencyBand', 'min')} />
              <ConfigInput label="Max Frequency" id="tfbMax" type="number" min={20} max={20000} step={10} value={livePresetConfig.targetFrequencyBand?.max} onChange={val => handleLiveConfigChange('targetFrequencyBand', val, true, true, 'targetFrequencyBand', 'max')} />
            </div>
            {/* Re-analyze button could be added here if audioBuffer and trackId were available */}
          </div>
        </div>
      )}

      {/* Debug Info */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Debug Info
          </h3>
          <button
            onClick={() => setShowDebugInfo(!showDebugInfo)}
            className="text-sm text-zinc-400 hover:text-white transition"
          >
            {showDebugInfo ? "Hide Details" : "Show Details"}
          </button>
        </div>

        {showDebugInfo && (
          <div className="bg-zinc-900 rounded-lg p-4 font-mono text-sm">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Debug Logs</h4>
              <button onClick={() => setLogs([])} className="text-xs text-zinc-400 hover:text-white">
                Clear
              </button>
            </div>
            <div className="h-48 overflow-y-auto space-y-1">
              {logs.map((log, index) => (
                <div key={index} className="text-xs text-zinc-400">
                  {log}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
